import api from '@/apis'
import { createLatestOnly } from '@/utils/latestOnly'
import { createDataVersion } from '@/utils/dataVersion'

// REQUEST OWNERSHIP — switching team starts a new request for the same card.
//
// The card also carries an IDENTITY (`team_id` below): WHICH team the list on screen belongs to.
// Ownership alone could not answer the question `updateUserTeam` needed — after a successful move
// it dispatched a refresh for the team selected when the edit began, and that refresh was the
// NEWEST request, so it legitimately won and painted team A's users under the still-selected
// team B. Identity is the same device the menu panels use (`route_id`).
const user_list_request = createLatestOnly()

// DATA CURRENCY — an accepted write, recorded so a read already in flight cannot put the old value
// back. Both edits on this card are optimistic `v-model` writes, so the row is already showing the
// new value locally; without this a refresh answered from a pre-update snapshot reverted it for
// good. See utils/dataVersion.js.
const user_data = createDataVersion()

export default {
  state: {
    loading: true,
    // WHICH TEAM THE CARD IS ABOUT — set when a read STARTS, not when it commits. Writing it only
    // on commit made it lag by a round trip: while the operator's newly selected team was still
    // loading it still named the team they had left, so `updateUserTeam`'s guard passed and its
    // refresh SUPERSEDED the read they were waiting for. Round 10 closed only half that window.
    team_id: null,
    headers: [
      { text: 'Avatar', value: 'avatar', sortable: false },
      { text: 'ID', value: 'user_id' },
      { text: 'Team', value: 'team', width: 140 },
      { text: 'First Name', value: 'firstname' },
      { text: 'Last Name', value: 'lastname' },
      { text: 'Last Login', value: 'last_login_datetime' },
      { text: 'Active', value: 'active' }
    ],
    items: []
  },
  mutations: {
    SET_ACCOUNT_INFO_CARD_LOADING(state, payload) {
      state.loading = payload
    },
    SET_ACCOUNT_INFO_CARD_TEAM(state, payload) {
      state.team_id = payload
    },
    SET_ACCOUNT_INFO_CARD_ITEMS(state, payload) {
      state.items = payload
    }
  },
  actions: {
    async fetchUserListByTeamID({ commit }, payload) {
      const isLatest = user_list_request()
      // Claimed at request start, like the token beside it: this is what the card is about now.
      commit('SET_ACCOUNT_INFO_CARD_TEAM', payload)
      const read = user_data.beginRead()
      try {
        const response = await api.getUsersByTeamID(payload)
        // A newer request of this kind owns the view now — see utils/latestOnly.js.
        if (!isLatest()) return
        commit('SET_SNACKBAR', { status: true, color: 'loading', text: 'Loading User List...', loading: true })
        if (response.data.success) {
          let users = response.data.data.map(user => {
            user.status = user.status === 1 ? true : false
            return user
          })
          commit(
            'SET_ACCOUNT_INFO_CARD_ITEMS',
            read.reconcile(users, user => user.user_id)
          )
          commit('SET_ACCOUNT_INFO_CARD_LOADING', false)
          commit('SET_SNACKBAR', { state: false })
        }
      } catch (error) {
        if (!isLatest()) return
        commit('SET_SNACKBAR', { status: true, color: 'error', text: 'Server Error', icon: 'close', timeout: '2000' })
      } finally {
        read.settle()
      }
    },
    async updateAccountStatus({ commit }, payload) {
      // The switch is `v-model`-bound, so the row already shows the new value. Recorded in the shape
      // the READ paints (`status` is mapped to a boolean there, the API takes 1 / 2).
      const { user_id, status } = (payload && payload.params) || {}
      const write = user_data.beginWrite(user_id, status === undefined ? {} : { status: status === 1 })
      let answered = false
      try {
        const response = await api.updateUsers(payload)
        answered = true
        commit('SET_SNACKBAR', { status: true, color: 'loading', text: 'Updating...', loading: true })
        if (response.data.success) {
          write.accepted()
          commit('SET_SNACKBAR', { status: true, color: 'success', text: 'Success', icon: 'check', timeout: '2000' })
        } else {
          commit('SET_SNACKBAR', { status: true, color: 'error', text: response.data.message, icon: 'close', timeout: '2000' })
        }
      } catch (error) {
        if (!answered) commit('SET_SNACKBAR', { status: true, color: 'error', text: 'Server Error', icon: 'close', timeout: '2000' })
      }
    },
    async updateUserTeam({ commit, dispatch, rootState }, payload) {
      try {
        const response = await api.updateUsers(payload)
        commit('SET_SNACKBAR', { status: true, color: 'loading', text: 'Updating...', loading: true })
        if (response.data.success) {
          commit('SET_SNACKBAR', { status: true, color: 'success', text: 'Success', icon: 'check', timeout: '2000' })
          // RECONCILE ONLY WHAT IS ON SCREEN. The refresh is itself a repaint of the one visible
          // team, so a move whose source team the operator has since left must NOT drag the card
          // back to it. Its change is real and committed; it is simply not displayed, and the next
          // visit to that team reads it fresh.
          if (rootState.accountInfoCard.team_id === payload.current_team) dispatch('fetchUserListByTeamID', payload.current_team)
        } else {
          commit('SET_SNACKBAR', { status: true, color: 'error', text: response.data.message, icon: 'close', timeout: '2000' })
        }
      } catch (error) {
        commit('SET_SNACKBAR', { status: true, color: 'error', text: 'Server Error', icon: 'close', timeout: '2000' })
      }
    }
  }
}
